import React, { useState } from 'react';
import useGroceryData from '../Hooks/useGroceryData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { RiArrowDownSLine, RiArrowUpSLine } from 'react-icons/ri';
import Slider from '../components/Slider';
import GroceryItemCard from '../components/GroceryItemCard';
import GroceryShimmer from '../components/GroceryShimmer';

const Groceries = () => {
  const {
    mainCategories,
    categoryGroups,
    loading,
    error,
    categoriesPagination,
    categoriesLoading,
    fetchMainCategories,
  } = useGroceryData();

  // State for selected category and its items (modal)
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // State for "Show more" in category groups
  const [expandedGroups, setExpandedGroups] = useState({});

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setShowCategoryModal(true);
  };

  const closeModal = () => {
    setShowCategoryModal(false);
    setSelectedCategory(null);
  };

  const toggleGroupExpand = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  if (loading) return <GroceryShimmer />;
  if (error) return <div className="text-center text-red-500">Failed to load groceries.</div>;

  return (
    <div className="body xl:max-w-[80%] mx-auto min-h-screen px-4 sm:px-6 md:px-0">
      {/* Header */}
      <div className="my-6">
        <h1 className="font-bold text-2xl md:text-3xl">Groceries</h1>
        <p className="text-gray-500 text-sm">Shop fresh groceries and essentials</p>
      </div>

      {/* Horizontal Scroll: Main Categories (like WOYM) */}
      {mainCategories.length > 0 && (
        <>
          <div className="flex justify-between items-center mx-0 md:mx-12 px-0 md:px-4 my-4">
            <h2 className="font-bold text-xl md:text-[1.7rem] leading-tight tracking-tight">
              Shop by Category
            </h2>
            <Slider className="foodCategory" amount={350} />
          </div>
          <div className="container-snap mx-0 md:mx-12 flex p-4 overflow-x-auto scrollbar-hide gap-4 mb-8">
            {mainCategories.map((category) => (
              <div
                key={category.id}
                className="cursor-pointer flex-shrink-0 text-center group transition-transform duration-300 hover:scale-105"
                onClick={() => handleCategoryClick(category)}
              >
                <div className="relative">
                  <img
                    className="bg-transparent h-[140px] w-[120px] md:h-[180px] md:w-[180px] object-contain"
                    src={category.image}
                    alt={category.name}
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/180x180?text=No+Image';
                    }}
                  />
                </div>
                <p className="mt-2 text-xs md:text-sm font-medium text-gray-700 group-hover:text-primary transition-colors duration-200">
                  {category.name}
                </p>
              </div>
            ))}
          </div>

          {/* Pagination for main categories (if needed) */}
          {categoriesPagination && categoriesPagination.last_page > 1 && (
            <div className="flex justify-center my-4">
              <button
                onClick={() => fetchMainCategories(categoriesPagination.current_page - 1)}
                disabled={!categoriesPagination.prev_page_url || categoriesLoading}
                className="px-4 py-2 border rounded-l bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2 border-t border-b bg-gray-100">
                {categoriesPagination.current_page} / {categoriesPagination.last_page}
              </span>
              <button
                onClick={() => fetchMainCategories(categoriesPagination.current_page + 1)}
                disabled={!categoriesPagination.next_page_url || categoriesLoading}
                className="px-4 py-2 border rounded-r bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Category Groups with subcategories */}
      {categoryGroups.length > 0 && (
        <>
          <hr className="my-8" />
          <div>
            <h2 className="font-bold text-xl md:text-[1.7rem] leading-tight tracking-tight mb-6">
              Explore Categories
            </h2>
          </div>

          {categoryGroups.map((group) => {
            const isExpanded = expandedGroups[group.id] || false;
            const displayedCategories = isExpanded ? group.categories : group.categories.slice(0, 11);

            return (
              <div key={group.id} className="mb-10">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">{group.name}</h3>
                <div className="flex flex-wrap gap-2 md:gap-x-2 justify-start my-4 px-4 md:px-0">
                  {displayedCategories.map((cat) => (
                    <div
                      key={cat.id}
                      className="
                        group-item
                        w-[48%] sm:w-[32%] md:w-[23%] lg:w-[18%]
                        flex-grow p-3 md:p-4 mb-3 md:mb-4
                        cursor-pointer border border-solid border-primary/80 rounded-xl
                        transition-all duration-200 hover:bg-primary/80 hover:border-primary
                      "
                      onClick={() => handleCategoryClick(cat)}
                    >
                      <div className="flex flex-col items-center">
                        {cat.image && (
                          <img
                            src={cat.image}
                            alt={cat.name}
                            className="w-12 h-12 object-contain mb-2"
                          />
                        )}
                        <h4
                          className="
                            truncate text-center text-sm md:text-base
                            text-[#050e1bbf] group-hover:text-white
                            tracking-tight font-medium transition-colors duration-200
                          "
                        >
                          {cat.name}
                        </h4>
                      </div>
                    </div>
                  ))}

                  {group.categories.length > 11 && (
                    <div
                      onClick={() => toggleGroupExpand(group.id)}
                      className="
                        w-[48%] sm:w-[32%] md:w-[23%] lg:w-[18%]
                        flex items-center justify-center flex-grow
                        p-3 md:p-4 mx-0 md:mx-2 mb-3 md:mb-4
                        cursor-pointer border border-solid border-primary/50 rounded-xl
                        transition-all duration-200 hover:bg-primary hover:text-white hover:border-primary
                      "
                    >
                      {isExpanded ? (
                        <>
                          <RiArrowUpSLine className="w-5 h-5 md:w-6 md:h-6 mr-1 md:mr-2" />
                          <h2 className="truncate text-center text-sm md:text-base tracking-tight font-medium">
                            Show less
                          </h2>
                        </>
                      ) : (
                        <>
                          <RiArrowDownSLine className="w-5 h-5 md:w-6 md:h-6 mr-1 md:mr-2" />
                          <h2 className="truncate text-center text-sm md:text-base tracking-tight font-medium">
                            Show more
                          </h2>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Modal: Category Items */}
      {showCategoryModal && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 p-4 border-b flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">{selectedCategory.name}</h2>
                <p className="text-gray-500 text-sm mt-1">
                  {selectedCategory.items?.length || 0} products available
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FontAwesomeIcon icon={faXmark} size="lg" />
              </button>
            </div>
            <div className="p-6">
              {selectedCategory.items && selectedCategory.items.length > 0 ? (
                <div className="grid place-items-center gap-4 md:gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {selectedCategory.items.map((item) => (
                    <GroceryItemCard key={item.id} item={item} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No products found in this category.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groceries;